// Copyright (c) StrangeBee 2024

import produce from 'immer';
import useLocalStorage, { LocalStorageKeys } from './useLocalStorage';
import { defaultStoragePageContent, StorageFilterEnum } from './useStoragePage';

export interface StorageView {
    [views: string]: any;
}

interface StorageViewList {
    [organisation: string]: {
        [fieldScope: string]: StorageView;
    };
}

type SetStorageType = React.SetStateAction<{
    [organisation: string]: {
        [fieldScope: string]: StorageView;
    };
} | null>;

export interface UseStorageViewReturn {
    views: StorageView;
    deleteView: (viewName: string) => string | null;
    saveView: (pageContent: any, viewName: string, oldViewName?: string) => void;
    setDefaultDisplay: (viewName: string, params: Record<string, boolean>) => void;
}

interface UseStorageViewParams {
    organisation: string;
    fieldScope?: StorageFilterEnum;
}

export const useStorageView = ({ organisation, fieldScope }: UseStorageViewParams): UseStorageViewReturn | null => {
    const { storage, setStorage } = useLocalStorage<StorageViewList | null>({
        key: LocalStorageKeys.VIEW,
        initialValue: (value) => {
            if (!fieldScope) return value;

            if (value && value[organisation] && value[organisation][fieldScope]) {
                return value;
            }
            return {
                ...value,
                [organisation]: {
                    ...value?.[organisation],
                    [fieldScope]: {
                        default: { ...defaultStoragePageContent, currentView: undefined },
                    },
                },
            };
        },
    });

    if (!fieldScope || !storage) return null;

    const saveView = (pageContent: any, viewName: string, oldViewName?: string): void => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { currentView, ...rest } = pageContent;
        const newStorage = { ...storage };
        newStorage[organisation][fieldScope] = { ...newStorage[organisation][fieldScope], [viewName]: rest };

        if (oldViewName) {
            delete Object.assign(newStorage[organisation][fieldScope], { [viewName]: rest })[oldViewName];
        }

        setStorage(newStorage);
    };

    const deleteView = (viewName: string): string | null => {
        const newStorage = { ...storage };
        const newStorageViews = { ...storage[organisation][fieldScope] };

        const remainingKeys = Object.keys(newStorage[organisation][fieldScope]).filter((view) => view !== viewName);

        if (remainingKeys.length !== 0) {
            delete newStorageViews[viewName];

            newStorage[organisation][fieldScope] = newStorageViews;
            setStorage({ ...newStorage });
            return remainingKeys[0];
        }

        return null;
    };

    const setDisplay = (viewName: string, attr: string, value: boolean): void =>
        setStorage(
            produce<SetStorageType>((draft) => {
                if (draft === null) return;
                if (!draft[organisation][fieldScope][viewName].display) {
                    draft[organisation][fieldScope][viewName].display = {};
                }
                draft[organisation][fieldScope][viewName].display[attr] = value;
            }),
        );

    const setDefaultDisplay = (viewName: string, params: Record<string, boolean>): void => {
        Object.entries(params).forEach(([attr, value]) => {
            if (typeof storage[organisation][fieldScope][viewName].display[attr] === 'undefined') {
                setDisplay(viewName, attr, value);
            }
        });
    };

    return {
        views: storage[organisation][fieldScope],
        deleteView,
        saveView,
        setDefaultDisplay,
    };
};
